import React, { useContext, useEffect, useState } from "react";
import DarkModeContext from "../../Contexts/DarkModeContext";
import usersService from "../../../services/UsersService";
import SwitchCom from "../../Helpers/SwitchCom";

// Same 4 sections/copy/behavior as myfit-app's AccountPreferencesScreen:
// Units, Color theme, Language, Home feed — each field saves immediately
// on change, same as every other real Settings tab in this app.
const UNIT_OPTIONS = [
  { value: "metric", label: "Metric (kg, km)" },
  { value: "imperial", label: "Imperial (lb, mi)" },
];

const THEME_OPTIONS = [
  { value: "system", label: "System" },
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
];

// myfit-users has no translation system yet (unlike myfit-app) — this
// still saves to the same account.language field the API/mobile app
// share, it just doesn't change any of this app's own UI text yet.
const LANGUAGE_OPTIONS = [
  { value: "en", label: "English" },
  { value: "fr", label: "Français" },
  { value: "es", label: "Español" },
];

const DEFAULTS = { units: "metric", theme: "system", language: "en" };

function SegmentedControl({ options, value, onChange, saving }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex border border-light-purple dark:border-[#5356fb29] rounded-lg overflow-hidden">
        {options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={`px-4 py-2 text-sm font-medium ${
              value === opt.value
                ? "bg-purple text-white"
                : "bg-[#FAFAFA] dark:bg-[#11131F] text-dark-gray dark:text-white"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
      {saving ? <span className="text-sm text-thin-light-gray">Saving…</span> : null}
    </div>
  );
}

export default function AccountPreferenceTab() {
  const api = new usersService();
  const { setThemePreference } = useContext(DarkModeContext);

  const [prefs, setPrefs] = useState(DEFAULTS);
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState(null);

  const [feedCategories, setFeedCategories] = useState([]);
  const [selectedFeedIds, setSelectedFeedIds] = useState([]);
  const [savingFeedId, setSavingFeedId] = useState(null);
  const [feedError, setFeedError] = useState(null);

  useEffect(() => {
    api
      .fetchAccountPreferences()
      .then((res) => {
        setPrefs(res.data);
        setThemePreference(res.data.theme);
      })
      .catch(() => setError("Could not load preferences."))
      .finally(() => setIsLoading(false));

    Promise.all([api.fetchHomeFeedCategories(), api.fetchHomeFeedPreferences()])
      .then(([categoriesRes, selectedRes]) => {
        setFeedCategories(categoriesRes.data.categories);
        setSelectedFeedIds(selectedRes.data.categories);
      })
      .catch(() => setFeedError("Could not load home feed options."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (key, value) => {
    const previous = prefs;
    setPrefs((p) => ({ ...p, [key]: value }));
    if (key === "theme") setThemePreference(value);
    setSaving(key);
    setError(null);
    try {
      await api.updateAccountPreferences({ [key]: value });
    } catch (err) {
      setPrefs(previous);
      if (key === "theme") setThemePreference(previous.theme);
      setError("Could not save. Try again.");
    } finally {
      setSaving(null);
    }
  };

  const toggleFeedCategory = async (id) => {
    const previous = selectedFeedIds;
    const next = previous.includes(id)
      ? previous.filter((c) => c !== id)
      : [...previous, id];
    setSelectedFeedIds(next);
    setSavingFeedId(id);
    setFeedError(null);
    try {
      await api.updateHomeFeedPreferences(next);
    } catch (err) {
      setSelectedFeedIds(previous);
      setFeedError("Could not save. Try again.");
    } finally {
      setSavingFeedId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="account-preference-tab w-full">
        <p className="table-empty">Loading…</p>
      </div>
    );
  }

  return (
    <div className="account-preference-tab w-full">
      {error && <p className="text-red-700 mb-4">{error}</p>}

      <div className="mb-6">
        <h2 className="text-sm tracking-wide uppercase font-bold text-thin-light-gray mb-3">
          Units
        </h2>
        <SegmentedControl
          options={UNIT_OPTIONS}
          value={prefs.units}
          onChange={(v) => save("units", v)}
          saving={saving === "units"}
        />
      </div>

      <div className="mb-6">
        <h2 className="text-sm tracking-wide uppercase font-bold text-thin-light-gray mb-3">
          Color theme
        </h2>
        <SegmentedControl
          options={THEME_OPTIONS}
          value={prefs.theme}
          onChange={(v) => save("theme", v)}
          saving={saving === "theme"}
        />
      </div>

      <div className="mb-6">
        <h2 className="text-sm tracking-wide uppercase font-bold text-thin-light-gray mb-3">
          Language
        </h2>
        <SegmentedControl
          options={LANGUAGE_OPTIONS}
          value={prefs.language}
          onChange={(v) => save("language", v)}
          saving={saving === "language"}
        />
      </div>

      <div>
        <h2 className="text-sm tracking-wide uppercase font-bold text-thin-light-gray mb-3">
          Home feed
        </h2>
        {feedError && <p className="text-red-700 mb-3">{feedError}</p>}
        {feedCategories.length === 0 ? (
          <p className="text-thin-light-gray text-sm">No categories available yet.</p>
        ) : (
          <ul className="w-full">
            {feedCategories.map((cat, i) => (
              <li
                key={cat.id}
                className={`flex justify-between items-center py-3.5 ${
                  i === feedCategories.length - 1
                    ? ""
                    : "border-b dark:border-[#5356fb29] border-light-purple"
                }`}
              >
                <span className="text-base text-dark-gray dark:text-white">{cat.label}</span>
                {savingFeedId === cat.id ? (
                  <span className="text-sm text-thin-light-gray">Saving…</span>
                ) : (
                  <SwitchCom
                    value={selectedFeedIds.includes(cat.id)}
                    handler={() => toggleFeedCategory(cat.id)}
                  />
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
